import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useKoreanBrief } from "../../../hooks/useKoreanBrief";
import { useKoreanBriefSettings } from "../../../hooks/useKoreanBriefSettings";
import { supabase, HeroBannerRow } from "../../../lib/supabase";

interface HeroSlide {
  img: string;
  sloganVi: string;
  sloganKr: string;
  subVi: string;
  subKr: string;
  // Visual style fields
  sloganColor: string;
  sloganFontFamily: string;
  sloganFontSize: number;
  sloganWhiteSpace: string;
  sloganX: number;
  sloganY: number;
  sloganLetterSpacing: number;
  sloganLineHeight: number;
  imageObjectFit: string;
  imageObjectPosition: string;
}

const DEFAULT_STYLE = {
  sloganColor: "#ffffff",
  sloganFontFamily: "inherit",
  sloganFontSize: 36,
  sloganWhiteSpace: "nowrap",
  sloganX: 10,
  sloganY: 15,
  sloganLetterSpacing: 0,
  sloganLineHeight: 1.2,
  imageObjectFit: "cover",
  imageObjectPosition: "center top",
};

const FALLBACK_SLIDES: HeroSlide[] = [
  {
    img: "https://readdy.ai/api/search-image?query=Korea%20Vietnam%20B2B%20technology%20trade%20bridge%20connection%20partnership%20two%20nations%20handshake%20business%20summit%20professional%20modern%20architectural%20abstract%20background%20dark%20navy%20blue%20deep%20atmosphere%20cinematic%20lighting&width=1920&height=580&seq=hero5_slide1_v2&orientation=landscape",
    sloganVi: "Công nghệ Hàn - Động lực Việt",
    sloganKr: "한국 기술 – 베트남의 성장 동력",
    subVi: "Nền tảng kết nối doanh nghiệp Hàn–Việt chuyên nghiệp, nơi công nghệ gặp gỡ cơ hội.",
    subKr: "한국 기술과 베트남 기회를 잇는 전문 B2B 플랫폼",
    ...DEFAULT_STYLE,
  },
  {
    img: "https://readdy.ai/api/search-image?query=strong%20bridge%20connecting%20two%20modern%20cities%20international%20business%20skyline%20Korea%20Vietnam%20cooperation%20economic%20growth%20premium%20cinematic%20aerial%20view%20golden%20hour%20blue%20sky%20professional%20photography&width=1920&height=580&seq=hero5_slide2_v2&orientation=landscape",
    sloganVi: "Value-Up Bridge: Bản sắc công nghệ, Đỉnh cao giá trị",
    sloganKr: "밸류업 브리지: 기술의 정체성, 가치의 정점",
    subVi: "Xây dựng cầu nối bền vững – nơi công nghệ Hàn Quốc tạo ra giá trị đỉnh cao.",
    subKr: "한-베 비즈니스 가치를 함께 높이는 견고한 가교",
    ...DEFAULT_STYLE,
    sloganWhiteSpace: "normal",
  },
  {
    img: "https://readdy.ai/api/search-image?query=Korean%20high-tech%20carbon%20nano%20material%20semiconductor%20industrial%20product%20catalog%20all%20information%20in%20Vietnamese%20language%20digital%20interface%20technology%20futuristic%20dark%20blue%20elegant%20background&width=1920&height=580&seq=hero5_slide3_v2&orientation=landscape",
    sloganVi: "Tiên phong công nghệ Hàn tại Việt Nam",
    sloganKr: "베트남의 한국 기술 선두주자",
    subVi: "Tất cả thông tin sản phẩm Hàn Quốc, được biên dịch hoàn toàn bằng tiếng Việt.",
    subKr: "모든 한국 상품 정보를 베트남어로 정확하게 전달합니다",
    ...DEFAULT_STYLE,
  },
  {
    img: "https://readdy.ai/api/search-image?query=Vietnam%20Ho%20Chi%20Minh%20city%20modern%20business%20district%20skyline%20meeting%20Korean%20products%20discover%20marketplace%20digital%20B2B%20platform%20contemporary%20professional%20deep%20blue%20night%20cityscape&width=1920&height=580&seq=hero5_slide4_v2&orientation=landscape",
    sloganVi: "Chạm công nghệ mới, Mở lối tương lai",
    sloganKr: "새로운 기술을 만지다, 미래를 열다",
    subVi: "Hàng nghìn sản phẩm công nghệ tiên tiến từ Hàn Quốc đang chờ bạn khám phá.",
    subKr: "수천 개의 한국 첨단 기술 제품을 지금 바로 만나보세요",
    ...DEFAULT_STYLE,
  },
  {
    img: "https://readdy.ai/api/search-image?query=one%20click%20digital%20connection%20business%20network%20B2B%20marketplace%20Korean%20Vietnamese%20entrepreneurs%20laptop%20smartphone%20technology%20seamless%20collaboration%20modern%20office%20dark%20elegant%20professional%20photography%20premium&width=1920&height=580&seq=hero5_slide5_v2&orientation=landscape",
    sloganVi: "Một chạm - Ngàn kết nối",
    sloganKr: "한 번의 클릭 – số lượng kết nối không giới hạn",
    subVi: "Đăng ký miễn phí, kết nối ngay với đối tác Hàn Quốc – nhanh chóng, đơn giản, hiệu quả.",
    subKr: "클릭 한 번으로 한국 기업과 베트남 비즈니스를 연결합니다",
    ...DEFAULT_STYLE,
  },
];

const MOCK_BRIEF = [
  { id: "m1", category: "Công nghệ", title: "Hàn Quốc dẫn đầu thế giới về xuất khẩu vật liệu carbon nano", time: "2 giờ trước", source_url: "" },
  { id: "m2", category: "Kinh tế", title: "Samsung và LG tăng đầu tư vào thị trường Việt Nam năm 2026", time: "5 giờ trước", source_url: "" },
  { id: "m3", category: "Thương mại", title: "Kim ngạch thương mại Hàn–Việt đạt kỷ lục 87 tỷ USD", time: "1 ngày trước", source_url: "" },
  { id: "m4", category: "Năng lượng", title: "Hàn Quốc ra mắt pin mặt trời hiệu suất 25%, giá thành giảm 40%", time: "2 ngày trước", source_url: "" },
];

const CATEGORY_VI: Record<string, string> = {
  tech: "Công nghệ", technology: "Công nghệ", economy: "Kinh tế", economic: "Kinh tế",
  industry: "Công nghiệp", industrial: "Công nghiệp", energy: "Năng lượng", trade: "Thương mại",
  science: "Khoa học", environment: "Môi trường", policy: "Chính sách", news: "Tin tức",
  startup: "Khởi nghiệp", finance: "Tài chính", material: "Vật liệu", materials: "Vật liệu",
  semiconductor: "Bán dẫn", it: "CNTT", health: "Y tế", construction: "Xây dựng",
  automotive: "Ô tô", mobility: "Giao thông", agriculture: "Nông nghiệp", food: "Thực phẩm",
  culture: "Văn hóa", education: "Giáo dục", chemical: "Hóa chất",
};

function translateCategory(cat: string): string {
  return CATEGORY_VI[cat.toLowerCase().trim()] || cat;
}

function bannerToSlide(b: HeroBannerRow): HeroSlide {
  return {
    img: b.image_url,
    sloganVi: b.slogan_vi,
    sloganKr: b.slogan_kr,
    subVi: b.sub_vi ?? "",
    subKr: b.sub_kr ?? "",
    sloganColor: b.slogan_color ?? "#ffffff",
    sloganFontFamily: b.slogan_font_family ?? "inherit",
    sloganFontSize: b.slogan_font_size ?? 36,
    sloganWhiteSpace: b.slogan_white_space ?? "nowrap",
    sloganX: b.slogan_x ?? 10,
    sloganY: b.slogan_y ?? 15,
    sloganLetterSpacing: b.slogan_letter_spacing ?? 0,
    sloganLineHeight: b.slogan_line_height ?? 1.2,
    imageObjectFit: b.image_object_fit ?? "cover",
    imageObjectPosition: b.image_object_position ?? "center top",
  };
}

const HeroSection = () => {
  useTranslation();
  const isKr = false;
  const [slides, setSlides] = useState<HeroSlide[]>(FALLBACK_SLIDES);
  const [activeSlide, setActiveSlide] = useState(0);
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();
  const { settings: briefSettings } = useKoreanBriefSettings();
  const articleCount = briefSettings.widget_article_count ?? 4;
  const { articles: briefArticles } = useKoreanBrief(articleCount);

  // Logo drag state
  const [logoDragging, setLogoDragging] = useState(false);
  const [logoPos, setLogoPos] = useState({ x: briefSettings.widget_logo_x ?? 50, y: briefSettings.widget_logo_y ?? 50 });
  const logoPanelRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; logoX: number; logoY: number } | null>(null);

  // Sync logoPos when settings load
  useEffect(() => {
    setLogoPos({ x: briefSettings.widget_logo_x ?? 50, y: briefSettings.widget_logo_y ?? 50 });
  }, [briefSettings.widget_logo_x, briefSettings.widget_logo_y]);

  const handleLogoDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setLogoDragging(true);
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      logoX: logoPos.x,
      logoY: logoPos.y,
    };
  }, [logoPos]);

  useEffect(() => {
    if (!logoDragging) return;
    const panel = logoPanelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();

    const onMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;
      const dx = e.clientX - dragStartRef.current.mouseX;
      const dy = e.clientY - dragStartRef.current.mouseY;
      const newX = Math.max(0, Math.min(100, dragStartRef.current.logoX + (dx / rect.width) * 100));
      const newY = Math.max(0, Math.min(100, dragStartRef.current.logoY + (dy / rect.height) * 100));
      setLogoPos({ x: newX, y: newY });
    };
    const onUp = () => setLogoDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [logoDragging]);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const { data } = await supabase
          .from("hero_banners")
          .select("*")
          .eq("is_active", true)
          .order("display_order", { ascending: true });
        if (data && data.length > 0) {
          setSlides((data as HeroBannerRow[]).map(bannerToSlide));
          setActiveSlide(0);
        }
      } catch { /* use fallback */ }
    };

    fetchBanners();

    const channel = supabase
      .channel("hero_banners_realtime_v2")
      .on("postgres_changes", { event: "*", schema: "public", table: "hero_banners" }, () => {
        fetchBanners();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const displayBrief = briefArticles.length > 0
    ? briefArticles.slice(0, articleCount).map((a) => ({
        id: a.id,
        category: translateCategory(a.category ?? "Tin tức"),
        title: a.title,
        time: a.published_at ? new Date(a.published_at).toLocaleDateString("vi-VN") : "",
        source_url: a.source_url ?? "",
      }))
    : MOCK_BRIEF.slice(0, articleCount);

  const goToSlide = (idx: number) => { setActiveSlide(idx); };
  const nextSlide = () => goToSlide((activeSlide + 1) % slides.length);
  const prevSlide = () => goToSlide((activeSlide - 1 + slides.length) % slides.length);

  useEffect(() => {
    autoRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 10000);
    return () => { if (autoRef.current) clearInterval(autoRef.current); };
  }, [slides.length]);

  return (
    <section className="relative overflow-hidden bg-[#001428]">
      <div className="relative h-[420px] md:h-[580px] overflow-hidden">
        {slides.map((s, i) => {
          const sloganText = s.sloganVi;
          const isActive = i === activeSlide;

          return (
            <div
              key={i}
              className="absolute inset-0"
              style={{
                opacity: isActive ? 1 : 0,
                transition: "opacity 1000ms ease-in-out",
                zIndex: isActive ? 1 : 0,
              }}
            >
              {/* Background image */}
              <img
                src={s.img}
                alt={s.sloganVi}
                className="w-full h-full"
                style={{
                  objectFit: s.imageObjectFit as "cover" | "contain",
                  objectPosition: s.imageObjectPosition,
                }}
              />

              {/* Gradient overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#001428]/95 via-[#001428]/50 to-[#001428]/20" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#001428]/80 via-[#001428]/30 to-transparent" />

              {/* ── SLOGAN OVERLAY - inside this slide ── */}
              <div
                className="absolute z-10 pointer-events-none"
                style={{
                  left: `${s.sloganX}%`,
                  top: `${s.sloganY}%`,
                  maxWidth: "80%",
                  animation: isActive ? "heroSlogan 0.8s cubic-bezier(0.22,1,0.36,1) forwards" : "none",
                  opacity: isActive ? undefined : 0,
                }}
              >
                {/* Slide counter accent */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-[2px] bg-[#00AEEF] rounded-full" />
                  <span className="text-[#00AEEF] text-[10px] font-bold tracking-[0.25em] uppercase">
                    {String(i + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
                  </span>
                </div>

                {/* Left accent bar + slogan text */}
                <div className="flex items-stretch gap-3">
                  <div className="w-[3px] bg-gradient-to-b from-[#00AEEF] to-[#004A99] rounded-full flex-shrink-0" />
                  <h2
                    style={{
                      color: s.sloganColor,
                      fontFamily: s.sloganFontFamily !== "inherit" ? s.sloganFontFamily : undefined,
                      fontSize: `clamp(18px, 3vw, ${s.sloganFontSize}px)`,
                      whiteSpace: s.sloganWhiteSpace as "nowrap" | "normal",
                      letterSpacing: `${s.sloganLetterSpacing}px`,
                      lineHeight: s.sloganLineHeight,
                      fontWeight: 900,
                      textShadow: "0 2px 16px rgba(0,0,0,0.9), 0 4px 32px rgba(0,0,0,0.7)",
                      margin: 0,
                    }}
                  >
                    {sloganText || "Đang tải slogan..."}
                  </h2>
                </div>
              </div>

              {/* ── Bottom-Left: Sub text + CTA ── */}
              <div
                className="absolute bottom-0 left-0 right-0 px-4 md:px-10 pb-8 md:pb-14 pointer-events-none"
                style={{
                  animation: isActive ? "heroBottom 0.75s cubic-bezier(0.22,1,0.36,1) forwards" : "none",
                  opacity: isActive ? undefined : 0,
                }}
              >
                <div className="max-w-[1400px] mx-auto w-full">
                  <div className="flex items-center gap-3 mb-2 md:mb-3">
                    <span className="w-10 h-[3px] bg-[#00AEEF] rounded-full" />
                    <span className="text-[#00AEEF] text-xs font-bold uppercase tracking-[0.2em]" style={{ textShadow: "0 1px 8px rgba(0,174,239,0.6)" }}>
                      🇰🇷 × 🇻🇳 &nbsp;Hiệp hội Carbon &amp; Nano (KCANIA) · BIZWELL
                    </span>
                  </div>
                  <p className="text-white/90 text-[13px] md:text-[15px] leading-relaxed max-w-lg hidden md:block" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.9), 0 2px 16px rgba(0,0,0,0.7)" }}>
                    {s.subVi}
                  </p>
                  <div className="mt-3 md:mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-2 md:gap-4 pointer-events-auto">
                    <span className="w-12 h-[3px] bg-[#00AEEF] rounded-full hidden lg:block" />
                    <Link to="/danh-muc" className="bg-[#004A99] hover:bg-[#003d7d] text-white text-xs md:text-sm px-4 md:px-7 py-2 md:py-3 rounded-lg font-bold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
                      <i className="ri-search-line" />
                      Khám Phá Sản Phẩm
                    </Link>

                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* ── Korean Brief Panel (right) - outside slides, always on top ── */}
        <div className="absolute right-4 top-3 bottom-3 w-[340px] flex-col z-20 hidden lg:flex">
          <div ref={logoPanelRef} className="bg-[#001428]/80 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden flex flex-col h-full relative">
            {/* Widget logo - draggable */}
            {briefSettings.widget_logo_url && (
              <div
                className={`absolute z-30 ${logoDragging ? "cursor-grabbing" : "cursor-grab"}`}
                style={{
                  left: `${logoPos.x}%`,
                  top: `${logoPos.y}%`,
                  transform: `translate(-50%, -50%) scale(${briefSettings.widget_logo_scale ?? 1})`,
                  userSelect: "none",
                }}
                onMouseDown={handleLogoDragStart}
                title="Kéo để di chuyển logo"
              >
                <img
                  src={briefSettings.widget_logo_url}
                  alt="Widget Logo"
                  className="h-10 w-auto object-contain pointer-events-none"
                  style={{ mixBlendMode: "screen" }}
                  draggable={false}
                />
              </div>
            )}

            <Link to="/korean-brief" className="bg-[#004A99] px-4 py-3 flex items-center justify-between flex-shrink-0 hover:bg-[#003d7d] transition-colors cursor-pointer">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center"><i className="ri-newspaper-line text-white text-base" /></div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="text-white font-bold whitespace-nowrap"
                      style={{
                        fontFamily: briefSettings.widget_font_family !== "inherit" ? briefSettings.widget_font_family : undefined,
                        fontSize: `${Math.min(briefSettings.widget_font_size ?? 14, 14)}px`,
                      }}
                    >
                      {briefSettings.widget_title || "Korean Brief"}
                    </span>
                    <span className="bg-[#00AEEF] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">DAILY</span>
                  </div>
                  <span
                    className="text-white/70 text-[10px] whitespace-nowrap tracking-wide"
                    style={{
                      fontFamily: briefSettings.widget_desc_font_family !== "inherit" ? briefSettings.widget_desc_font_family : undefined,
                    }}
                  >
                    {briefSettings.widget_subtitle || "Điểm tin Hàn Quốc"}
                  </span>
                </div>
              </div>
              <span className="text-white/50 text-[10px] whitespace-nowrap">Tiếng Việt</span>
            </Link>

            <div className="flex-1 px-3 py-2 overflow-hidden flex flex-col justify-around">
              {displayBrief.map((news, idx) => (
                <div
                  key={news.id}
                  onClick={() => navigate(briefArticles.length > 0 ? `/korean-brief/${news.id}` : "/korean-brief")}
                  className="flex gap-2 group py-2 border-b border-white/5 last:border-0 cursor-pointer items-start"
                >
                  {/* Category badge */}
                  <span
                    className="flex-shrink-0 font-bold text-[#00AEEF] bg-[#00AEEF]/10 rounded text-center leading-tight"
                    style={{
                      width: 64,
                      minWidth: 64,
                      maxWidth: 64,
                      fontSize: `${Math.min(briefSettings.widget_desc_font_size ?? 10, 10)}px`,
                      fontFamily: briefSettings.widget_desc_font_family !== "inherit" ? briefSettings.widget_desc_font_family : undefined,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      display: "block",
                      padding: "2px 4px",
                      lineHeight: 1.3,
                    }}
                  >
                    {news.category}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-white/85 leading-snug group-hover:text-white transition-colors line-clamp-2"
                      style={{
                        fontSize: `${Math.min(briefSettings.widget_desc_font_size ?? 12, 12)}px`,
                        fontFamily: briefSettings.widget_desc_font_family !== "inherit" ? briefSettings.widget_desc_font_family : undefined,
                      }}
                    >
                      {news.title}
                    </p>
                    <p className="text-white/35 text-[10px] mt-0.5">{news.time}</p>
                  </div>
                  {idx === 0 && <span className="flex-shrink-0 text-[8px] font-black text-white bg-red-500/80 px-1 py-0.5 rounded h-fit whitespace-nowrap">HOT</span>}
                </div>
              ))}
            </div>

            <Link to="/korean-brief" className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between flex-shrink-0 hover:bg-white/5 transition-colors cursor-pointer">
              <span className="text-white/60 text-xs">Tin tức Hàn Quốc mới nhất</span>
              <span className="text-[#00AEEF] text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
                Xem tất cả <i className="ri-arrow-right-line text-xs" />
              </span>
            </Link>
          </div>
        </div>

        {/* Navigation arrows */}
        <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-full transition-all cursor-pointer text-white backdrop-blur-sm z-20">
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button onClick={nextSlide} className="absolute right-[368px] top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-full transition-all cursor-pointer text-white backdrop-blur-sm z-20 hidden lg:flex">
          <i className="ri-arrow-right-s-line text-xl" />
        </button>

        {/* Slide dots */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 items-center z-20">
          {slides.map((_, i) => (
            <button key={i} onClick={() => goToSlide(i)} className={`rounded-full transition-all cursor-pointer ${i === activeSlide ? "w-7 h-2 bg-[#00AEEF]" : "w-2 h-2 bg-white/40 hover:bg-white/60"}`} />
          ))}
        </div>
      </div>
      <div className="h-1 bg-gradient-to-r from-[#004A99] via-[#00AEEF] to-[#004A99]" />

      {/* ── Mobile Korean Brief (below hero, only on mobile) ── */}
      <div className="lg:hidden bg-[#001428] px-4 pb-4">
        <Link to="/korean-brief" className="bg-[#004A99] px-4 py-3 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-2">
            <i className="ri-newspaper-line text-white text-lg" />
            <span
              className="text-white font-bold text-sm"
              style={{
                fontFamily: briefSettings.widget_font_family !== "inherit" ? briefSettings.widget_font_family : undefined,
                fontSize: `${Math.min(briefSettings.widget_font_size ?? 16, 16)}px`,
              }}
            >
              {briefSettings.widget_title || "Korean Brief"}
            </span>
            <span className="bg-[#00AEEF] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">DAILY</span>
          </div>
          <span className="text-white/60 text-xs">{briefSettings.widget_subtitle || "Điểm tin Hàn Quốc"}</span>
        </Link>
        <div className="bg-[#001428]/90 border border-white/10 border-t-0 rounded-b-xl divide-y divide-white/5">
          {displayBrief.slice(0, Math.min(articleCount, 3)).map((news) => (
            <div
              key={news.id}
              onClick={() => navigate(briefArticles.length > 0 ? `/korean-brief/${news.id}` : "/korean-brief")}
              className="flex gap-2 px-3 py-2.5 cursor-pointer"
            >
              <span
                className="flex-shrink-0 font-bold text-[#00AEEF] bg-[#00AEEF]/10 px-1.5 py-0.5 rounded text-[10px] text-center leading-tight"
                style={{ width: 72, minWidth: 72, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}
              >
                {news.category}
              </span>
              <p className="text-white/80 text-xs leading-snug flex-1 min-w-0 break-words">
                {news.title}
              </p>
            </div>
          ))}
          <Link to="/korean-brief" className="flex items-center justify-center gap-1 py-2.5 text-[#00AEEF] text-xs font-semibold">
            Xem tất cả <i className="ri-arrow-right-line text-xs" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
